import { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Character } from "@/mocks/characters";
import CharacterForm from "@/pages/admin/components/CharacterForm";
import AdminSettings from "@/pages/admin/components/AdminSettings";

interface Props {
  characters: Character[];
  onSave: (chars: Character[], reorderOnly?: boolean) => Promise<void>;
  onToggleActiveOptimistic: (id: string) => void;
  onLogout: () => void;
}

// Sortable character row
interface SortableRowProps {
  char: Character;
  onEdit: (char: Character) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
}

const SortableCharacterRow = ({ char, onEdit, onDelete, onToggleActive }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: char.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-2xl border p-4 flex items-center gap-4 transition-colors ${
        char.isActive === true ? "border-pink-100" : "border-gray-200 opacity-60"
      } ${isDragging ? "border-pink-300 bg-pink-50/50" : ""}`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-pink-400 cursor-grab active:cursor-grabbing flex-shrink-0 transition-colors"
        title="ドラッグして並び替え"
      >
        <i className="ri-draggable text-xl"></i>
      </button>

      <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-pink-200 flex-shrink-0">
        <img
          src={char.images[0] || "https://public.readdy.ai/ai/img_res/56e8f00d84a330496e43401a9dd67dea.jpg"}
          alt={char.name}
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-bold text-gray-800">{char.name}</h3>
          <span className="text-pink-400 text-sm">{char.age != null ? `${char.age}歳` : "ひみつ"}</span>
          {char.type && (
            <span className="text-xs bg-pink-50 text-pink-400 px-2 py-0.5 rounded-full">{char.type}</span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full ${char.isActive === true ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"}`}>
            {char.isActive === true ? "表示中" : "非表示"}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          {char.area && (
            <>
              <i className="ri-map-pin-line text-pink-400"></i>{char.area}
            </>
          )}
          <span className="ml-2"><i className="ri-image-line text-pink-400"></i>{char.images.length}枚</span>
        </p>
        <p className="text-xs text-gray-400 truncate mt-0.5">{char.link}</p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => onToggleActive(char.id)}
          className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
            char.isActive === true
              ? "bg-green-100 text-green-600 hover:bg-green-200"
              : "bg-gray-100 text-gray-400 hover:bg-gray-200"
          }`}
          title={char.isActive === true ? "非表示にする" : "表示する"}
        >
          <i className={char.isActive === true ? "ri-eye-fill" : "ri-eye-off-fill"}></i>
        </button>
        <button
          onClick={() => onEdit(char)}
          className="w-9 h-9 flex items-center justify-center bg-pink-50 text-pink-500 rounded-full hover:bg-pink-100 transition-all cursor-pointer"
          title="編集"
        >
          <i className="ri-edit-line"></i>
        </button>
        <button
          onClick={() => onDelete(char.id)}
          className="w-9 h-9 flex items-center justify-center bg-red-50 text-red-400 rounded-full hover:bg-red-100 transition-all cursor-pointer"
          title="削除"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </div>
    </div>
  );
};

const AdminDashboard = ({ characters, onSave, onToggleActiveOptimistic, onLogout }: Props) => {
  const [activeTab, setActiveTab] = useState<"characters" | "preview" | "settings">("characters");
  const [view, setView] = useState<"list" | "add" | "edit">("list");
  const [editTarget, setEditTarget] = useState<Character | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleAdd = (char: Character) => {
    const newChars = [...characters, char];
    onSave(newChars);
    setView("list");
    showToast("キャラクターを追加しました！");
  };

  const handleEdit = (char: Character) => {
    const newChars = characters.map((c) => (c.id === char.id ? char : c));
    onSave(newChars);
    setView("list");
    setEditTarget(null);
    showToast("キャラクターを更新しました！");
  };

  const handleDelete = (id: string) => {
    const newChars = characters.filter((c) => c.id !== id);
    onSave(newChars);
    setDeleteConfirm(null);
    showToast("キャラクターを削除しました");
  };

  const handleToggleActive = (id: string) => {
    onToggleActiveOptimistic(id);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = characters.findIndex((c) => c.id === active.id);
    const newIndex = characters.findIndex((c) => c.id === over.id);
    const reordered = arrayMove(characters, oldIndex, newIndex);

    setIsSavingOrder(true);
    await onSave(reordered, true);
    setIsSavingOrder(false);
    showToast("並び順を保存しました！");
  };

  const filteredCharacters = searchQuery.trim()
    ? characters.filter(
        (c) =>
          c.name.includes(searchQuery) ||
          (c.area && c.area.includes(searchQuery)) ||
          (c.type && c.type.includes(searchQuery)) ||
          String(c.age).includes(searchQuery)
      )
    : characters;

  if (view === "add") {
    return (
      <CharacterForm
        onSubmit={handleAdd}
        onCancel={() => setView("list")}
        mode="add"
      />
    );
  }

  if (view === "edit" && editTarget) {
    return (
      <CharacterForm
        initial={editTarget}
        onSubmit={handleEdit}
        onCancel={() => { setView("list"); setEditTarget(null); }}
        mode="edit"
      />
    );
  }

  return (
    <div className="min-h-screen bg-pink-50" style={{ fontFamily: "'Nunito', sans-serif" }}>
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-pink-500 text-white px-6 py-3 rounded-full text-sm font-medium animate-fade-in">
          <i className="ri-check-line mr-2"></i>{toast}
        </div>
      )}

      {/* Saving order indicator */}
      {isSavingOrder && (
        <div className="fixed top-6 right-6 z-50 bg-white border border-pink-200 text-pink-500 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i>保存中...
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-pink-100 px-4 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="https://public.readdy.ai/ai/img_res/db459360-20e7-4f18-828f-b6f77daa6c55.png"
            alt="Girls' Map"
            className="h-8 w-auto object-contain"
          />
          <span className="font-bold text-pink-500" style={{ fontFamily: "'Pacifico', cursive" }}>
            Girls&apos; Map 管理画面
          </span>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-pink-500 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-logout-box-line"></i>ログアウト
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-pink-100 px-4 md:px-8">
        <div className="flex gap-0">
          {[
            { key: "characters", label: "キャラクター管理", icon: "ri-user-heart-line" },
            { key: "preview", label: "ホームプレビュー", icon: "ri-eye-line" },
            { key: "settings", label: "サイト設定", icon: "ri-settings-3-line" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as "characters" | "settings")}
              className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-pink-400 text-pink-500"
                  : "border-transparent text-gray-500 hover:text-pink-400"
              }`}
            >
              <i className={tab.icon}></i>{tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {activeTab === "characters" && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: "総キャラ数", value: characters.length, icon: "ri-user-heart-fill", color: "text-pink-500" },
                { label: "表示中", value: characters.filter((c) => c.isActive === true).length, icon: "ri-eye-fill", color: "text-green-500" },
                { label: "非表示", value: characters.filter((c) => c.isActive !== true).length, icon: "ri-eye-off-fill", color: "text-gray-400" },
                { label: "画像総数", value: characters.reduce((sum, c) => sum + c.images.length, 0), icon: "ri-image-fill", color: "text-rose-400" },
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 border border-pink-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">{stat.label}</span>
                    <div className="w-8 h-8 flex items-center justify-center bg-pink-50 rounded-full">
                      <i className={`${stat.icon} ${stat.color} text-sm`}></i>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* List Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800">キャラクター管理</h2>
                <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                  <i className="ri-draggable text-pink-300"></i>
                  左のアイコンをドラッグして並び順を変更できます
                </p>
              </div>
              <button
                onClick={() => setView("add")}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-400 to-rose-400 text-white rounded-full text-sm font-semibold hover:from-pink-500 hover:to-rose-500 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>キャラを追加
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                <i className="ri-search-line text-pink-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="名前・エリア・タイプ・年齢で検索..."
                className="w-full pl-10 pr-10 py-2.5 bg-white border border-pink-200 rounded-full text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-pink-400 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-xs text-gray-400 mb-3">
                「{searchQuery}」の検索結果：{filteredCharacters.length}件
              </p>
            )}

            {/* Character List with DnD */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={characters.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-3">
                  {filteredCharacters.map((char) => (
                    <SortableCharacterRow
                      key={char.id}
                      char={char}
                      onEdit={(c) => { setEditTarget(c); setView("edit"); }}
                      onDelete={(id) => setDeleteConfirm(id)}
                      onToggleActive={handleToggleActive}
                    />
                  ))}

                  {filteredCharacters.length === 0 && (
                    <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border border-pink-100">
                      <i className="ri-search-line text-4xl mb-3 block"></i>
                      <p className="text-sm">
                        {searchQuery ? `「${searchQuery}」に一致するキャラクターが見つかりません` : "キャラクターがいません。追加してみましょう！"}
                      </p>
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </>
        )}

        {activeTab === "preview" && (
          <div>
            <div className="mb-4">
              <h2 className="text-lg font-bold text-gray-800">ホームページプレビュー</h2>
              <p className="text-xs text-gray-400 mt-0.5">「人気の女の子」セクションに表示される順番のプレビューです（表示中の最大8人）</p>
            </div>
            <div className="bg-white rounded-2xl border border-pink-100 p-6">
              <div className="flex items-center gap-2 mb-5">
                <span className="inline-block bg-pink-50 text-pink-500 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full">✦ Popular Girls ✦</span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {characters.filter((c) => c.isActive === true).slice(0, 8).map((girl, idx) => (
                  <div key={girl.id} className="relative bg-white rounded-2xl overflow-hidden border border-pink-100">
                    <div className="absolute top-2 left-2 z-10 w-6 h-6 flex items-center justify-center bg-pink-500 text-white text-xs font-black rounded-full">{idx + 1}</div>
                    <div className="relative w-full overflow-hidden" style={{ height: "180px" }}>
                      <img src={girl.images[0]} alt={girl.name} className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <div className="absolute top-2 right-2 bg-white/90 text-pink-600 text-xs px-2 py-0.5 rounded-full font-black">{girl.age != null ? `${girl.age}歳` : "ひみつ"}</div>
                      <div className="absolute bottom-0 left-0 right-0 p-2">
                        <h3 className="text-white font-black text-sm">{girl.name}</h3>
                        <span className="bg-pink-500/80 text-white text-xs px-2 py-0.5 rounded-full">{girl.type}</span>
                      </div>
                    </div>
                    <div className="p-2">
                      <p className="text-xs text-gray-500 line-clamp-2">{girl.catchphrase}</p>
                    </div>
                  </div>
                ))}
                {characters.filter((c) => c.isActive === true).length === 0 && (
                  <div className="col-span-4 text-center py-12 text-gray-400">
                    <i className="ri-user-heart-line text-4xl mb-2 block"></i>
                    <p className="text-sm">表示中のキャラクターがいません</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-4 text-center">
                <i className="ri-information-line mr-1"></i>
                並び順はキャラクター管理タブのドラッグ＆ドロップで変更できます
              </p>
            </div>
          </div>
        )}

        {activeTab === "settings" && (
          <AdminSettings characters={characters} />
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="text-center mb-4">
              <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-3">
                <i className="ri-delete-bin-line text-red-400 text-2xl"></i>
              </div>
              <h3 className="font-bold text-gray-800 mb-1">本当に削除しますか？</h3>
              <p className="text-sm text-gray-500">この操作は取り消せません。</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 bg-red-400 text-white rounded-full text-sm font-medium hover:bg-red-500 transition-all cursor-pointer whitespace-nowrap"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
